import { useState, useEffect, useMemo } from "react";
import { Modal, Grid, Box } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField/FormField";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import PlantOptions from "modules/plants/PlantOptions";
import useAddPlantProductToProposalProduct from "hooks/proposals/useAddPlantProductToProposalProduct";
import currencyEndAdornment from "constants/currencyEndAdornment";
import { ProposalProduct, ProductType } from "generated/graphql";
import { materialNeededCalculations } from "DDD/action-objects/MaterialNeededCalculations";
import { applicationRateMappings } from "modules/products/data/applicationRateMappings";
import { useTonConversionFactor } from "hooks/utils/useTonConversionFactor";

interface AddMaterialModalProps {
  open: boolean;
  onClose: () => void;
  product: ProposalProduct;
}

interface FormData {
  plantId: string;
  plantProductId: string;
  applicationRate: number | null;
  tonConversionFactor: number | null;
  materialNeeded: number;
  rate: number | null;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

export default function AddMaterialModal({ open, onClose, product }: AddMaterialModalProps) {
  const [selectedPlantId, setSelectedPlantId] = useState<string>("");
  const [selectedPlantProductId, setSelectedPlantProductId] = useState<string>("");

  const [addPlantProductToProposalProduct, { loading }] = useAddPlantProductToProposalProduct();

  const defaultValues = useMemo(
    () => ({
      plantId: "",
      plantProductId: "",
      applicationRate: (product.material.attributes as any)?.applicationRate || null,
      tonConversionFactor: null,
      materialNeeded: 0,
      rate: null,
    }),
    [product]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    values: defaultValues,
  });

  const watchedApplicationRate = watch("applicationRate");
  const tonConversionFactor = useTonConversionFactor(product, watchedApplicationRate);
  const watchedPlantId = watch("plantId");
  const watchedPlantProductId = watch("plantProductId");

  useEffect(() => {
    setValue("tonConversionFactor", tonConversionFactor);
  }, [tonConversionFactor, setValue]);

  const productType = product.material.type as ProductType;

  const shouldShowSelectForApplicationRate =
    productType.toUpperCase() === ProductType.ASPHALT ||
    productType.toUpperCase() === ProductType.ASPHALT_CURBING ||
    productType.toUpperCase() === ProductType.EXCAVATE_OUT;

  const applicationRateOptions = shouldShowSelectForApplicationRate
    ? applicationRateMappings[productType.toUpperCase() as ProductType] || []
    : [];

  useEffect(() => {
    const uppercaseProductType = productType.toUpperCase() as ProductType;
    const calculationFn = materialNeededCalculations[uppercaseProductType];
    if (!calculationFn) return;

    const needsTonConversion = [
      ProductType.ASPHALT,
      ProductType.ASPHALT_CURBING,
      ProductType.EXCAVATE_OUT,
    ];
    const needsApplicationRate = [
      ProductType.CONCRETE_FLATWORK,
      ProductType.MICROSURFACING,
      ProductType.GRADING,
      ProductType.SLURRY_SEAL,
      ProductType.CONCRETE_WATERWAY,
    ];

    let result = 0;
    if (needsTonConversion.includes(uppercaseProductType)) {
      // Pass applicationRate as optional 3rd param (used by ASPHALT_CURBING)
      result = (calculationFn as any)(
        product,
        tonConversionFactor || 0,
        watchedApplicationRate || 0
      );
    } else if (needsApplicationRate.includes(uppercaseProductType)) {
      result = (calculationFn as any)(product, watchedApplicationRate || 0);
    } else {
      result = (calculationFn as any)(product);
    }

    setValue("materialNeeded", result > 0 ? parseFloat(result.toFixed(2)) : 0);
  }, [
    product,
    productType,
    tonConversionFactor,
    watchedApplicationRate,
    setValue,
    watchedPlantId,
    watchedPlantProductId,
  ]);

  // Reset plant product selection when plant changes
  useEffect(() => {
    if (watchedPlantId) {
      setValue("plantProductId", "");
      setSelectedPlantProductId("");
    }
  }, [watchedPlantId, setValue]);

  const onSubmit = async (data: FormData) => {
    const result = await addPlantProductToProposalProduct({
      variables: {
        input: {
          proposalProductId: product.id,
          plantId: selectedPlantId,
          plantProductId: data.plantProductId,
          applicationRate: data.applicationRate,
          materialNeededOverride: data.materialNeeded,
          rateOverride: data.rate,
        },
      },
    });

    if (result.data) {
      reset();
      setSelectedPlantId("");
      setSelectedPlantProductId("");
      onClose();
    }
  };

  const handleClose = () => {
    reset();
    setSelectedPlantId("");
    setSelectedPlantProductId("");
    onClose();
  };

  const AddMaterialForm = ({
    plantOptions,
    plantProductOptions,
    loading,
    search,
    plants,
    pagination,
  }) => {
    // Find selected plant product rate
    useEffect(() => {
      if (watchedPlantProductId && selectedPlantId) {
        const selectedPlant = plants.find((plant) => plant.id === selectedPlantId);
        if (selectedPlant) {
          const selectedPlantProduct = selectedPlant.products.find(
            (product) => product.id === watchedPlantProductId
          );
          if (selectedPlantProduct) {
            setValue("rate", selectedPlantProduct.rate); // The value is in cents
          }
        }
      } else {
        setValue("rate", null);
      }
    }, [watchedPlantProductId, selectedPlantId, plants, setValue]);

    return (
      <MDBox component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="plantId"
              control={control}
              rules={{ required: "Plant is required" }}
              render={({ field, fieldState: { error } }) => (
                <ScrollAsyncAutocomplete
                  options={plantOptions}
                  value={{
                    label: plantOptions.find((x) => x.value === field.value)?.label ?? "",
                    value: field.value,
                  }}
                  onChange={(value) => {
                    setSelectedPlantId(String(value));
                    field.onChange(value);
                  }}
                  loading={loading}
                  error={error?.message ?? ""}
                  label="Plant"
                  search={search}
                  onLoadMore={pagination.loadMore}
                  hasMore={pagination.hasNextPage}
                  variant="standard"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="plantProductId"
              control={control}
              rules={{ required: "Plant product is required" }}
              render={({ field, fieldState: { error } }) => (
                <AutoComplete
                  disabled={!selectedPlantId}
                  options={plantProductOptions[selectedPlantId] ?? []}
                  field={{
                    ...field,
                    onChange: (value) => {
                      setSelectedPlantProductId(value);
                      field.onChange(value);
                    },
                  }}
                  loading={loading}
                  error={(error?.message ?? "") as string}
                  label="Plant Product"
                  search={search}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            {shouldShowSelectForApplicationRate ? (
              <CustomSelect
                label="Depth"
                control={control}
                fullWidth
                selectVariant="standard"
                data={applicationRateOptions}
                name="applicationRate"
              />
            ) : (
              <FormField
                label="Depth"
                placeholder={
                  (product.material.attributes as any)?.applicationRate?.toString() || "0"
                }
                error={!!errors.applicationRate}
                helperText={errors.applicationRate?.message}
                {...register("applicationRate", {
                  valueAsNumber: true,
                })}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <FormField
              label="Conversion Factor or Weight"
              disabled
              {...register("tonConversionFactor")}
            />
          </Grid>

          <Grid item xs={12}>
            <FormField
              label="Tons Needed"
              placeholder="Calculated tons"
              error={!!errors.materialNeeded}
              helperText={errors.materialNeeded?.message}
              {...register("materialNeeded", {
                required: "Tons needed is required",
                valueAsNumber: true,
                min: { value: 0.01, message: "Must be greater than 0" },
              })}
            />
          </Grid>

          <Grid item xs={12}>
            <CurrencyFormField
              label="Rate"
              placeholder={0}
              {...currencyEndAdornment}
              control={control}
              name="rate"
              error={!!errors.rate}
              helperText={errors.rate?.message}
            />
          </Grid>
        </Grid>

        <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <MDButton variant="outlined" color="secondary" onClick={handleClose}>
            Cancel
          </MDButton>
          <MDButton type="submit" variant="gradient" color="success" disabled={loading}>
            {loading ? "Adding..." : "Add Material"}
          </MDButton>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <MDTypography variant="h4" component="h2" gutterBottom>
          Confirm Material Specs
        </MDTypography>

        <PlantOptions>
          {({ plantOptions, plantProductOptions, loading, search, plants, pagination }) => (
            <AddMaterialForm
              plantOptions={plantOptions}
              plantProductOptions={plantProductOptions}
              loading={loading}
              search={search}
              plants={plants}
              pagination={pagination}
            />
          )}
        </PlantOptions>
      </Box>
    </Modal>
  );
}
